import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ShoppingBag, TrendingUp, AlertCircle, BarChart3 } from 'lucide-react';
import { getProducts } from '@/data/products';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cfc143ec-270d-4208-9592-8eae45ed356e/artifacts/4ltgv78b_Untitled%20design-2.png';

const AdminDashboard = () => {
  const [salesReport, setSalesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);

  useEffect(() => {
    generateReports();
  }, []);

  const generateReports = () => {
    // Generate reports from local data
    const products = getProducts();
    
    // Sales report (demo data)
    setSalesReport({
      total_revenue: 0,
      total_orders: 0,
      pending_orders: 0,
      confirmed_orders: 0,
      shipped_orders: 0,
      delivered_orders: 0
    });
    
    // Inventory report (from local products)
    const totalProducts = products.length;
    const lowStock = products.filter(p => p.stock > 0 && p.stock <= 10).length;
    const outOfStock = products.filter(p => p.stock === 0).length;
    const totalValue = products.reduce((sum, p) => sum + (p.price * p.stock), 0);
    
    setInventoryReport({
      total_products: totalProducts,
      low_stock_products: lowStock,
      out_of_stock_products: outOfStock,
      total_stock_value: totalValue
    });
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }} data-testid="admin-dashboard">
      {/* Sidebar */}
      <div className="admin-sidebar">
        <div style={{ padding: '0 2rem', marginBottom: '2rem' }}>
          <img src={LOGO_URL} alt="Om Jishnu" style={{ height: '50px', marginBottom: '0.5rem' }} />
          <h2 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#A44A66' }}>ADMIN PANEL</h2>
        </div>
        <Link to="/admin" className="admin-sidebar-link active" data-testid="admin-nav-dashboard">
          <BarChart3 size={20} style={{ marginRight: '0.75rem', display: 'inline' }} />
          Dashboard
        </Link>
        <Link to="/admin/products" className="admin-sidebar-link" data-testid="admin-nav-products">
          <Package size={20} style={{ marginRight: '0.75rem', display: 'inline' }} />
          Products
        </Link>
        <Link to="/admin/orders" className="admin-sidebar-link" data-testid="admin-nav-orders">
          <ShoppingBag size={20} style={{ marginRight: '0.75rem', display: 'inline' }} />
          Orders
        </Link>
        <div style={{ padding: '2rem', borderTop: '1px solid #e5e5e5', marginTop: '2rem' }}>
          <Link to="/" style={{ color: '#666', textDecoration: 'none', fontSize: '0.9rem' }} data-testid="back-to-site">← Back to Site</Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="admin-main">
        <h1 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '2rem' }} data-testid="dashboard-title">Dashboard</h1>

        {/* Sales Report */}
        <div style={{ marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid="sales-report-title">
            <TrendingUp size={28} style={{ color: '#A44A66' }} />
            Sales Report
          </h2>
          {salesReport && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="total-revenue-card">
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Revenue</p>
                <p style={{ fontSize: '2rem', fontWeight: '700', color: '#10b981' }} data-testid="total-revenue">₹{salesReport.total_revenue.toLocaleString()}</p>
              </div>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="total-orders-card">
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Orders</p>
                <p style={{ fontSize: '2rem', fontWeight: '700', color: '#A44A66' }} data-testid="total-orders">{salesReport.total_orders}</p>
              </div>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="pending-orders-card">
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Pending</p>
                <p style={{ fontSize: '2rem', fontWeight: '700', color: '#f59e0b' }} data-testid="pending-orders">{salesReport.pending_orders}</p>
              </div>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="confirmed-orders-card">
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Confirmed</p>
                <p style={{ fontSize: '2rem', fontWeight: '700', color: '#3b82f6' }} data-testid="confirmed-orders">{salesReport.confirmed_orders}</p>
              </div>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="shipped-orders-card">
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Shipped</p>
                <p style={{ fontSize: '2rem', fontWeight: '700', color: '#8b5cf6' }} data-testid="shipped-orders">{salesReport.shipped_orders}</p>
              </div>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="delivered-orders-card">
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Delivered</p>
                <p style={{ fontSize: '2rem', fontWeight: '700', color: '#10b981' }} data-testid="delivered-orders">{salesReport.delivered_orders}</p>
              </div>
            </div>
          )}
        </div>

        {/* Inventory Report */}
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid="inventory-report-title">
            <Package size={28} style={{ color: '#A44A66' }} />
            Inventory Report
          </h2>
          {inventoryReport && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="total-products-card">
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Products</p>
                <p style={{ fontSize: '2rem', fontWeight: '700', color: '#A44A66' }} data-testid="total-products">{inventoryReport.total_products}</p>
              </div>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="low-stock-card">
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Low Stock</p>
                <p style={{ fontSize: '2rem', fontWeight: '700', color: '#f59e0b' }} data-testid="low-stock">{inventoryReport.low_stock_products}</p>
              </div>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="out-of-stock-card">
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Out of Stock</p>
                <p style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444' }} data-testid="out-of-stock">{inventoryReport.out_of_stock_products}</p>
              </div>
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="stock-value-card">
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Stock Value</p>
                <p style={{ fontSize: '2rem', fontWeight: '700', color: '#10b981' }} data-testid="stock-value">₹{inventoryReport.total_stock_value.toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;